<template>
    <VueFinalModal
        class="AskForVolunteerModal flex justify-center items-center"
        content-class="flex flex-col max-w-2xl mx-4 p-6 bg-white dark:bg-surface-card border dark:border-default rounded-lg max-h-[90vh] overflow-hidden"
        :content-transition="isScrollable ? 'vfm-fade' : 'vfm-fade'"
        @update:model-value="onModalModelUpdate"
    >
        <div class="relative">
            <button 
                aria-label="Close modal"
                class="modal-close absolute right-0 top-0 text-black dark:text-white hover:text-gray-600 dark:hover:text-txt-body z-10 text-4xl"
                @click="emit('close')"
            >
                ×
            </button>
            <h1 class="text-3xl font-semibold mb-4 shrink-0">Záujem o dobrovoľníka</h1>
        </div>
        
        <div class="flex-1 overflow-y-auto">
            <div v-if="projects.length && !hasSubmitted" class="space-y-4">
            <CFormGroup
                class="mb-10"
                :error="validationResults.errors.projectId"
                label="Vyber projekt, do ktorého chceš dobrovoľníka zapojiť."
                :required="true"
            >
                <CFormRadio v-model="formData.projectId" :options="projectOptions" />
            </CFormGroup>
            
            <CFormGroup
                description="Dobrovoľník sa bude môcť lepšie rozhodnúť či bude s tebou spolupracovať, keď bude vedieť aká bude jeho náplň práce, aké sú tvoje očakávania, prečo by mal práve tebe pomôcť."
                :error="validationResults.errors.motivation"
                label="Napíš správu dobrovoľníkovi, prečo by si chcel/a spolupracovať práve s ním/s ňou."
                :required="true"
            >
                <CFormTextarea v-model="formData.motivation" />
            </CFormGroup>

            <CAlert class="h-fit lg:w-full" variant="info">
                <p class="mb-0">
                    TIP: Na základe tejto správy sa dobrovoľník rozhodne, či spoluprácu s tebou na projekte prijať alebo
                    nie, preto si na nej daj záležať. Tvoje šance môžeš zvýšiť, ak:
                </p>
                <ul class="list-inside list-disc">
                    <li>máš poriadne popísaný svoj dobrovoľnícky projekt</li>
                    <li>konkrétne napíšeš ako by ti vedel tento človek pomôcť</li>
                    <li>ak vysvetlíš svoju motiváciu, teda prečo oslovuješ práve tohto človeka</li>
                </ul>
            </CAlert>
            </div>

            <div v-else-if="!hasSubmitted" class="text-center">
                <p>
                    Nato, aby si mohol <strong>osloviť dobrovoľníka</strong>, musíš si najskôr vytvoriť svoj prvý projekt.
                </p>
            </div>
        </div>

        <div class="flex justify-end space-x-2 pt-4 border-t shrink-0">
            <CButton
                v-if="projects.length && !hasSubmitted"
                class="ml-auto w-full sm:w-auto"
                :disabled="isSubmitting"
                :loading="isSubmitting"
                size="large"
                variant="primary"
                @click.prevent="submit"
            >
                Odoslať
            </CButton>
            <CButton
                v-else
                class="ml-auto w-full sm:w-auto"
                size="large"
                :to="{ name: 'profile-projects-create' }"
                variant="secondary"
                @click="close"
            >
                Vytvoriť projekt / organizáciu
            </CButton>
        </div>
    </VueFinalModal>
</template>

<script setup lang="ts">
import { VueFinalModal, useModal } from 'vue-final-modal'
import { validate, isRequired, type ValidationResults } from 'last-validator'

import { isRichtextRequired } from '@/validators/isRichtextRequired'
import type { ParticipationRequest, ParticipationRequestFormData } from '#shared/types/ParticipationRequest'
import type { Project } from '#shared/types/Project'
import { VisibilityType } from '#shared/types/visibility'
import type { Option } from '@/components/ui/CFormRadio.vue'
import TooMuchRequestsModal from '@/components/modals/TooMuchRequestsModal.vue'
import { useAuth } from '@/composables/auth/useAuth'
import { useChat } from '@/composables/chat/useChat'
import { useOrgs } from '@/composables/orgs/useOrgs'
import { useProjects } from '@/composables/projects/useProjects'


// Props
const props = defineProps({
    volunteerId: {
        type: String,
        required: true
    }
})

// Emits
const emit = defineEmits<{
    (e: 'close'): void
    (e: 'update:modelValue', value: boolean): void
}>()

function onModalModelUpdate(val: boolean) {
    if (!val) emit('close')
}

// Composables
const router = useRouter()
const { getUserId } = useAuth()
const { sendMessage } = useChat()
const { loadUserOrgs } = useOrgs()
const {
    createVolunteerRequest,
    resolveParticipationRequestChat,
    getUserParticipationRequestsByOrgMemberProjects,
    listProjectsByOrgId
} = useProjects()

// Modal setup
const { open: openTooMuchRequestsModal, close: closeTooMuchRequestsModal } = useModal({
    component: TooMuchRequestsModal
})

// Reactive state
const projects = ref<Project[]>([])
const isSubmitting = ref(false)
const hasSubmitted = ref(false)
const formData = ref<ParticipationRequestFormData>({
    projectId: '',
    motivation: ''
})
const projectOptions = ref<Option[]>([])
const trackGtmEvent = ref(false)
const validationResults = ref<ValidationResults>({
    isValid: true,
    errors: {}
})

// Computed
const isScrollable = computed((): boolean => {
    return Boolean(projects.value.length && !hasSubmitted.value)
})

// Methods
async function validateForm(data: ParticipationRequestFormData) {
    return await validate<ParticipationRequestFormData>(data, {
        projectId: [isRequired()],
        motivation: [isRichtextRequired()]
    })
}

async function submit() {
    trackGtmEvent.value = true
    isSubmitting.value = true

    try {
        validationResults.value = await validateForm({
            ...formData.value
        })

        if (!validationResults.value.isValid) {
            isSubmitting.value = false
            return
        }

        const request = await createVolunteerRequest(props.volunteerId, formData.value)

        const chat = await resolveParticipationRequestChat(request)
        sendMessage(chat.id, request.motivation)
        hasSubmitted.value = true

        emit('close')
        if (formData.value.projectId) {
            router.push({
                name: 'dobrovolnictvo-ako-dalej-organizacia-id',
                params: { id: formData.value.projectId }
            })
        }
    } catch (e: any) {
        if (e.message === 'Request failed with status code 429') {
            isSubmitting.value = false
            close()
            await openTooMuchRequestsModal()
            return
        }
        console.error('Submit project request', e)
    } finally {
        isSubmitting.value = false
    }
}

function closeModalWithRedirect() {
    close()
    if (formData.value.projectId) {
        router.push({
            name: 'dobrovolnictvo-ako-dalej-organizacia-id',
            params: { id: formData.value.projectId }
        })
    }
}

function close() {
    emit('close')
}

// Lifecycle
onMounted(async () => {
    try {
        const userOrgsIds = (await loadUserOrgs()).map(({ id }) => id)
        
        // Only fetch projects if user has organizations (Firebase doesn't allow empty arrays in 'in' filters)
        if (userOrgsIds.length > 0) {
            // Deleted and unpublished projects can't be offered to a volunteer at all
            projects.value = (await listProjectsByOrgId(userOrgsIds)).filter(
                (project) => !project.deletedAt && project.visibility !== VisibilityType.hidden
            )
        } else {
            projects.value = []
        }
        // Requests only drive the disabled state of options — if their load fails,
        // still render the projects so the user can submit the form
        let requests: ParticipationRequest[] = []
        try {
            requests = await getUserParticipationRequestsByOrgMemberProjects(getUserId(), props.volunteerId, projects.value)
        } catch (error) {
            console.error('Failed to fetch participation requests:', error)
        }

        const isProjectDisabled = (project: Project): boolean => {
            return requests.some(({ projectId }) => projectId === project.id)
        }

        const resolveDisabledReason = (project: Project): string => {
            return isProjectDisabled(project) ? 'Dobrovoľník už je zapojený v projekte' : ''
        }
        projectOptions.value = projects.value
            .map((project: Project) => ({
                value: project.id,
                label: project.title!,
                disabled: isProjectDisabled(project),
                tooltip: resolveDisabledReason(project)
            }))
            .sort((a: Option, b: Option) => Number(a.disabled) - Number(b.disabled))
    } catch (error) {
        console.error('Failed to fetch projects:', error)
    }
})

// Head meta
useHead(() => {
    if (!trackGtmEvent.value) return {}

    return {
        script: [
            {
                innerHTML: `window.dataLayer = window.dataLayer || []; window.dataLayer.push({event: 'user_request', request_type: 'volunteer'})`,
                type: 'text/javascript'
            }
        ]
    }
})
</script>
